import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../client";
import type { DiseaseArea, DiseaseAreaCreate, DiseaseAreaUpdate } from "../types";

const KEY = ["disease-areas"] as const;

export function useDiseaseAreas() {
  return useQuery<DiseaseArea[]>({
    queryKey: KEY,
    queryFn: () => apiRequest("/api/disease-areas"),
  });
}

export function useDiseaseArea(id: string | undefined) {
  return useQuery<DiseaseArea>({
    queryKey: [...KEY, id],
    queryFn: () => apiRequest(`/api/disease-areas/${id}`),
    enabled: Boolean(id),
  });
}

export function useCreateDiseaseArea() {
  const qc = useQueryClient();
  return useMutation<DiseaseArea, Error, DiseaseAreaCreate>({
    mutationFn: (payload) =>
      apiRequest("/api/disease-areas", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateDiseaseArea(id: string) {
  const qc = useQueryClient();
  return useMutation<DiseaseArea, Error, DiseaseAreaUpdate>({
    mutationFn: (payload) =>
      apiRequest(`/api/disease-areas/${id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      }),
    onSuccess: (da) => {
      qc.setQueryData([...KEY, id], da);
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteDiseaseArea() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (id) => apiRequest(`/api/disease-areas/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}
